import React, { useState } from 'react'
import type { AccountHolderAddress } from '@mobi/api-types'
import { formatAddress, createFullSuburb } from '@mobi/utils'
import { AddressManualEntry } from './AddressManualEntry'
import { ManualInputTextStyled, ManualEntryLabel } from './AddressInput.styled'
import { Input } from '@mobi/component-library/Selection&Input/Inputs'
import {
  AutocompleteInputField,
  AutocompleteInputProps,
} from '@mobi/component-library/Legacy/Input/AutocompleteInputField/AutocompleteInputField'
import {
  getRequiredStreetNumberValidationMessage,
  getRequiredValidationMessage,
} from '@mobi/account/Areas/SignUp/Utils'
import { useAutoCompleteAddressList } from './Hooks/useAutoCompleteAddressList'
import { useSplitAddress } from './Hooks/useSplitAddress'
import type { ValidationResult } from './types'

type EntryMode = 'Autocomplete' | 'Manual'

export type AddressInputProps = {
  label?: string
  prefix?: string
  address?: AccountHolderAddress
  defaultEntryMode?: EntryMode
  required?: boolean
  onEntryModeChanged?: (mode: EntryMode) => void
  onValidationChange: (result: ValidationResult<AccountHolderAddress>) => void
  isDisabled?: boolean
  onFocus?: () => void
  onBlur?: () => void
  showManualEntryLabel?: boolean
}

export const AddressInput = ({
  label,
  prefix = '',
  address,
  defaultEntryMode = 'Autocomplete',
  required = true,
  onEntryModeChanged,
  onValidationChange,
  isDisabled = false,
  onFocus,
  onBlur,
  showManualEntryLabel,
}: AddressInputProps) => {
  const searchAddresses = useAutoCompleteAddressList()
  const [entryMode, setEntryMode] = useState<EntryMode>(defaultEntryMode)
  const [autocompleteValidationMessage, setAutocompleteValidationMessage] = useState('')

  const splitAddress = useSplitAddress({
    onSuccess: (addressDetails: AccountHolderAddress) => {
      const validationMessage = !addressDetails.streetNumber
        ? 'We require a street number. Please try another address or enter your address manually.'
        : ''
      setAutocompleteValidationMessage(validationMessage)
      onValidationChange({
        isValid: !validationMessage,
        result: addressDetails,
      })
    },
    onError: () => {
      setAutocompleteValidationMessage(
        'Unable to confirm address. Please try again or enter manually.'
      )
      onValidationChange({
        isValid: false,
      })
    },
  })

  const onSelectAutocompleteAddress = (addressMonikerId: string) => {
    setAutocompleteValidationMessage('')
    splitAddress.mutate(addressMonikerId)
  }

  if (entryMode === 'Autocomplete') {
    return (
      <div>
        <AutocompleteInputField
          name={label ?? 'Address'}
          id={`${prefix}address`}
          inputName={`${prefix}address`}
          placeholder='Enter address'
          autoComplete='street-address'
          defaultValue={
            address
              ? formatAddress({
                  unitNumber: address.unitNumber,
                  streetNumber: address.streetNumber,
                  streetName: address.streetName,
                  streetType: address.streetType,
                  suburbStateAndPostcode: createFullSuburb(address),
                })
              : ''
          }
          required={required}
          fetchSuggestionsCallback={searchAddresses}
          onSelect={addressMonikerId => onSelectAutocompleteAddress(addressMonikerId)}
          onUnselect={() => onValidationChange({ isValid: false })}
          renderInput={({
            inputName,
            name,
            ...props
          }: Omit<AutocompleteInputProps, 'defaultValue' | 'type'>) => (
            <Input
              {...props}
              name={inputName ?? 'autocomplete'}
              label={name}
              autoComplete='new-password'
              isDisabled={isDisabled || splitAddress.isPending}
              onFocus={() => {
                setAutocompleteValidationMessage('')
                onFocus?.()
              }}
              onBlur={() => {
                const validationMessage =
                  getRequiredValidationMessage(props.value) ||
                  getRequiredStreetNumberValidationMessage(
                    splitAddress.data ? splitAddress.data.streetNumber : address?.streetNumber
                  )
                setAutocompleteValidationMessage(validationMessage)
                onBlur?.()
              }}
              isInvalid={!!autocompleteValidationMessage}
              helpText={autocompleteValidationMessage}
            />
          )}
        />
        <ManualInputTextStyled>
          Having trouble?{' '}
          <span
            role='button'
            tabIndex={isDisabled ? -1 : 0}
            onClick={event => {
              if (isDisabled) {
                event.preventDefault()
                return
              }
              setEntryMode('Manual')
              onEntryModeChanged?.('Manual')
              event.preventDefault()
            }}
            aria-disabled={isDisabled}
          >
            Enter your address manually
          </span>
        </ManualInputTextStyled>
      </div>
    )
  }

  if (entryMode === 'Manual') {
    return (
      <>
        {showManualEntryLabel && <ManualEntryLabel>{label ?? 'Address'}</ManualEntryLabel>}

        <AddressManualEntry
          defaultValue={address}
          inputNamePrefix={prefix}
          onValidationChange={onValidationChange}
          isDisabled={isDisabled}
          onFocus={onFocus}
          onBlur={onBlur}
        />

        <ManualInputTextStyled>
          <span
            role='button'
            tabIndex={isDisabled ? -1 : 0}
            onClick={event => {
              if (isDisabled) {
                event.preventDefault()
                return
              }
              setEntryMode('Autocomplete')
              onEntryModeChanged?.('Autocomplete')
              event.preventDefault()
            }}
            aria-disabled={isDisabled}
          >
            Search for your address
          </span>
        </ManualInputTextStyled>
      </>
    )
  }

  return null
}
